<template>
  <section>
    <div class="d-flex justify-content-between mb-3">
      <div>
        <h2 class="mt-4">Insights</h2>
        <span v-if="selectedCapability" class="tag">{{ selectedCapability }}</span>
      </div>
      <div class="d-flex justify-content-between align-items-center gap-1">
        <div>
          <button class="btn" style="padding: 10px" @click="copyUrlToClipboard()">
            <IconShare />
          </button>
        </div>
        <slot name="close" />
      </div>
    </div>

    <div class="d-flex align-items-center modal-filters">
      <div class="flex-grow-1">
        <div class="row filters-gap-x">
          <div v-if="selectedCapability && usePageStore().page?.system.type != contentTypes.capabilities_page.codename
" class="col-md-6 col-lg-3">
            <div class="dropdown">
              <div
                id="dropdownMenuButtonCapability"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ selectedCapability || 'Capability' }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonCapability">
                <li key="Capability">
                  <a class="dropdown-item" href="#"> Capability </a>
                </li>
                <li :key="selectedCapability">
                  <a class="dropdown-item" href="#">
                    {{ selectedCapability }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="dropdown">
              <div
                id="dropdownMenuButtonInsightCategory"
                class="form-select"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {{ selectedInsightCategory || 'Insight Category' }}
              </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonInsightCategory">
                <li key="Insight Category">
                  <a class="dropdown-item" href="#"> Insight Category </a>
                </li>
                <li v-for="(term, i) in availableCategories" :key="`term-${i}`">
                  <a
                    class="dropdown-item"
                    href="#"
                    @click.prevent="changeSelectedInsightCategory(term)"
                  >
                    {{ term }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="dropdown">
              <div
              id="dropdownMenuButtonAuthor"
              class="form-select"
              type="button"
              data-bs-toggle="dropdown"
              aria-expanded="false"
              :class="{ 'disabled': usePageStore().page?.system.type == contentTypes.people_page.codename }"
              :aria-disabled="usePageStore().page?.system.type == contentTypes.people_page.codename"
            >
              {{ selectedAuthor || 'Author' }}
            </div>
              <ul class="dropdown-menu" aria-labelledby="dropdownMenuButtonAuthor">
                <li key="Author">
                  <a class="dropdown-item" href="#"> Author </a>
                </li>
                <li v-for="author in authors" :key="author">
                  <a class="dropdown-item" href="#" @click.prevent="changeSelectedAuthor(author)">
                    {{ author }}
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div class="col-md-6 col-lg-3">
            <div class="d-flex justify-content-between">
              <div class="search-input-container w-100">
                <IconMagnifyingGlass />
                <input
                  v-model="searchValue"
                  class="form-control"
                  type="text"
                  placeholder="Search for title, keyword..."
                  :disabled="isLoading"
                  @input="debouncedSearch"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-shrink-0">
        <button
          class="d-none d-lg-inline-block btn btn-light clear-all flex-grow-1 text-nowrap align-self-center"
          style="padding-right: 2px"
          @click="clearFilters()"
        >
          Clear All
        </button>
      </div>
    </div>

    <div class="d-flex justify-content-between mb-2 align-items-center">
      <div class="d-none d-lg-inline-block results">Showing {{ totalResults }} Result(s)</div>
      <button
        class="d-lg-none d-inline-block btn btn-light clear-all flex-grow-1 text-nowrap text-start ps-0"
        style="padding-right: 2px"
        @click="clearFilters()"
      >
        Clear All
      </button>
      <div class="d-flex align-items-center">
        <span class="d-none d-sm-inline-block sort-dropdown-title">Sort by</span>
        <select
          v-model="selectedSortValue"
          class="btn btn-light sort-dropdown px-0 pe-2"
          @change="changeSortValue(selectedSortValue)"
        >
          <option value="desc">New to Old</option>
          <option value="asc">Old to New</option>
        </select>
        <div class="sort-buttons">
          <button
            v-element-hover="onGridBtnHovered"
            :class="{ 'sort-view sort-grid': true, active: isGrid }"
            @click="insightStore.setIsGrid(true)"
          >
            <IconSquaresFour v-if="isGrid || isGridBtnHovered" />
            <IconSquaresFourInactive v-else />
          </button>
          <button
            v-element-hover="onListBtnHovered"
            :class="{ 'sort-view sort-list': true, active: !isGrid }"
            @click="insightStore.setIsGrid(false)"
          >
            <IconRowsInactive v-if="!isGrid" />
            <template v-else>
              <IconRows v-if="!isListBtnHovered" />
              <IconRowsHovered v-else />
            </template>
          </button>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup lang="ts">
import debounce from 'lodash/debounce'
import { ref } from 'vue'
import { useInsightModalStore } from '~/stores/modals/insight-modal'
import { vElementHover } from '@vueuse/components'
import { useToast } from 'vue-toast-notification';
import { contentTypes } from '~/models';

const insightStore = useInsightModalStore()
const {
  isGrid,
  selectedAuthor,
  selectedSortValue,
  totalResults,
  isLoading,
  authors,
  availableCategories,
  selectedCapability,
  selectedInsightCategory
} = storeToRefs(insightStore)

const searchValue = ref(insightStore.searchValue)
const debouncedSearch = debounce(() => {
  insightStore.setSearchValue(searchValue.value)
  usePaginationStore().changePage(1)
}, 1000)

function changeSelectedAuthor(author: string) {
  insightStore.setSelectedAuthor(author)
}

function changeSortValue(value: string) {
  insightStore.setSortValue(value)
}

function clearFilters() {
  insightStore.clearFilters()
}

function changeSelectedInsightCategory(category: string) {
  insightStore.setSelectedInsightCategory(category)
}

async function copyUrlToClipboard() {
  const queryParams = {
    c: insightStore.selectedCapability || '',
      a: insightStore.selectedAuthor || '',
      i: insightStore.selectedInsightCategory || '',
      s: insightStore.searchValue || ''
  };
  const url = new URL(insightsPageUrl ,window.location.origin);
  Object.keys(queryParams).forEach((key) => {
    const queryKey = key as keyof typeof queryParams;
    if (queryParams[queryKey]) {
      url.searchParams.append(queryKey, queryParams[queryKey]);
    }
  });
  const toast = useToast();
  try {
    await navigator.clipboard.writeText(url.toString());
    
    toast.success('URL copied to clipboard.', {
      position:'bottom-right'
    });
  } catch (error) {
    console.error('Failed to copy the URL: ', error);
    toast.error('Failed to copy the URL.', {
      position:'bottom-right'
    })
  }
}
const isGridBtnHovered = ref(false)
function onGridBtnHovered(state: boolean) {
  isGridBtnHovered.value = state
}
const isListBtnHovered = ref(false)
function onListBtnHovered(state: boolean) {
  isListBtnHovered.value = state
}

watch( () => insightStore.searchValue, () => {
  searchValue.value = insightStore.searchValue
})
</script>
